import React from 'react';
import type { LayerGeometryType, LayerRenderer, RendererCategory } from '../../../types';
import ColorSwatchPicker from './ColorSwatchPicker';
import CategorySymbolPopover from './CategorySymbolPopover';

/**
 * Editable list of unique-value classes.
 *
 * Each row owns its own colour AND symbol — a point class can pick its own
 * icon, a line class its own dash style, a polygon class its own fill
 * pattern, not just a different colour of the one shared symbol. Editing a
 * row replaces ONLY that entry — the renderer's global symbol and every
 * other class are left untouched (see CategorySymbolPopover / mergeCategoryStyle).
 *
 * Keys are index-based on purpose: category values are not guaranteed unique
 * once coerced to strings (e.g. "63 mm" and "63  mm" render identically), and
 * duplicate React keys break reconciliation so edits land on the wrong row.
 */
export default function UniqueValueClassList({
  categories,
  defaultColor,
  geometry,
  renderer,
  sldOnly = false,
  expanded = false,
  onCategoryChange,
  onDefaultColorChange,
}: {
  categories: RendererCategory[];
  defaultColor: string;
  geometry: LayerGeometryType;
  /** The layer's own renderer — categories inherit any symbol field they
   *  don't override from here (see mergeCategoryStyle). */
  renderer: LayerRenderer;
  /** GeoServer renders WMS itself and only understands SLD marks. */
  sldOnly?: boolean;
  /** True while the Symbology panel is maximised — more room to show every
   *  class at once instead of a tall, cramped scroll list. */
  expanded?: boolean;
  onCategoryChange: (index: number, changes: Partial<RendererCategory>) => void;
  onDefaultColorChange: (color: string) => void;
}) {
  return (
    <div className="rounded-lg border border-slate-200">
      <div className="flex items-center justify-between border-b border-slate-100 px-2 py-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          {categories.length} {categories.length === 1 ? 'class' : 'classes'}
        </span>
        <span className="text-[10px] text-slate-400">Tap to edit</span>
      </div>

      <div
        className={`overflow-y-auto p-1.5 ${
          expanded ? 'max-h-[70vh] grid grid-cols-2 gap-x-2 gap-y-0.5 content-start' : 'max-h-48 space-y-0.5'
        }`}
      >
        {categories.map((category, index) => (
          <div
            key={`class-${index}`}
            className="flex items-center gap-2 rounded-md px-1 py-1 transition hover:bg-slate-50"
          >
            <CategorySymbolPopover
              geometry={geometry}
              renderer={renderer}
              category={category}
              sldOnly={sldOnly}
              label={category.label || category.value || '(blank)'}
              onChange={(changes) => onCategoryChange(index, changes)}
            />
            <div className="min-w-0 flex-1">
              <p
                className="truncate text-[11px] font-medium text-slate-700"
                // Tooltip exposes the underlying value, which may differ from
                // the displayed label.
                title={`Value: ${category.value || '(blank)'}`}
              >
                {(category.label || category.value) === '' ? (
                  <em className="text-slate-400">(blank)</em>
                ) : (
                  category.label || category.value
                )}
              </p>
              <p className="font-mono text-[9px] uppercase text-slate-400">{category.color}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Catch-all class for values not present in the list. */}
      <div className="flex items-center gap-2 border-t border-slate-100 px-2.5 py-1.5">
        <ColorSwatchPicker
          color={defaultColor}
          label="All other values"
          onChange={onDefaultColorChange}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[11px] italic text-slate-500">All other values</p>
          <p className="font-mono text-[9px] uppercase text-slate-400">{defaultColor}</p>
        </div>
      </div>
    </div>
  );
}
